import React, { useState } from 'react';
import { BusinessAddUser, BusinessAddAdminUserDto } from '../Businesses/BusinessAddUser';
import { BusinessList } from '../Businesses/BusinessList';
import { ImportBusinessEntity } from '../Businesses/ImportBusinessEntity';
import KaAlert from '../Common/KaAlert';
import QueryView from '../Common/QueryView';
import useDisplayMessage from '../Common/useDisplayMessage';
import { CreateBusiness } from '../Businesses/CreateBusiness';
import { KaEmployeeTemplate } from '../UserAccess/KaEmployeeTemplate';
import { useKaEmployee } from '../UserAccess/useKaEmployee';
import { ManageSites } from '../Businesses/ManageSites';
import DisableSites from '../Businesses/DisableSites';
import { CreateEditSite } from './CreateEditSite';
import { HttpMethod } from '../Common/useFetch';
import useKaEmployeeRequest, { KaEmployeeRequestSpec } from '../Common/useKaEmployeeRequest';
import useToggleableItems from '../Common/useToggleableItems';
import useGetBusinesses, { BusinessDto, SiteDto } from '../../requests/employee/useGetBusinesses';

enum States {
  None,
  AddUser,
  ImportProducts,
  ImportDrivers,
  ImportCustomers,
  ImportCarriers,
  ManageSites,
  CreateSite,
  EditSite,
  DisableSites,
  EnableSites,
  CreateBusiness
}

type PopupState =
  {
    state: States.None;
  } |
  {
    state: States.AddUser;
    Business: BusinessDto;
  } |
  {
    state: States.ImportProducts;
    Business: BusinessDto;
  } |
  {
    state: States.ImportDrivers;
    Business: BusinessDto;
  } |
  {
    state: States.ImportCustomers;
    Business: BusinessDto;
  } |
  {
    state: States.ImportCarriers;
    Business: BusinessDto;
  } |
  {
    state: States.ManageSites;
    Business: BusinessDto;
  } |
  {
    state: States.CreateSite;
    Business: BusinessDto;
  } |
  {
    state: States.EditSite;
    Site: SiteDto;
  } |
  {
    state: States.DisableSites;
    Business: BusinessDto;
  } |
  {
    state: States.EnableSites;
    Business: BusinessDto;
  } |
  {
    state: States.CreateBusiness;
  };

const enableSitesSpec = (businessId?: string): KaEmployeeRequestSpec => ({
  path: `/employeeApi/businesses/${businessId}/enable`,
  method: HttpMethod.POST,
});

const BusinessPage = () => {
  const kahlerUser = useKaEmployee();
  const displayMessage = useDisplayMessage();

  const getBusinesses = useGetBusinesses();
  const expanded = useToggleableItems();
  const [popupState, setPopupState] = useState<PopupState>({ state: States.None });

  const businessId = popupState.state != States.None
    && popupState.state != States.CreateBusiness
    && popupState.state != States.EditSite
    ? popupState.Business.id
    : '';

  const useEnableSites = useKaEmployeeRequest<void>(enableSitesSpec(businessId), {
    onSuccess: () => {
      displayMessage.success("Sites enabled successfully");
      getBusinesses.query();
    },
    onError: (e) => displayMessage.fail(e.message),
  });

  const addUserToBusiness = (business: BusinessDto) => {
    displayMessage.clear();
    setPopupState({ state: States.AddUser, Business: business });
  }

  const importProducts = (business: BusinessDto) => {
    displayMessage.clear();
    setPopupState({ state: States.ImportProducts, Business: business });
  }

  const importDrivers = (business: BusinessDto) => {
    displayMessage.clear();
    setPopupState({ state: States.ImportDrivers, Business: business });
  }

  const importCustomers = (business: BusinessDto) => {
    displayMessage.clear();
    setPopupState({ state: States.ImportCustomers, Business: business });
  }

  const importCarriers = (business: BusinessDto) => {
    displayMessage.clear();
    setPopupState({ state: States.ImportCarriers, Business: business });
  }

  const manageSites = (business: BusinessDto) => {
    displayMessage.clear();
    setPopupState({ state: States.ManageSites, Business: business });
  }

  const createSite = (business: BusinessDto) => {
    displayMessage.clear();
    setPopupState({ state: States.CreateSite, Business: business });
  }

  const createSiteSuccess = (customerName: string) => {
    displayMessage.success(`Site "${customerName}" created`);
    clearState();
    getBusinesses.query();
  }

  const editSite = (site: SiteDto) => {
    displayMessage.clear();
    setPopupState({ state: States.EditSite, Site: site });
  }

  const editSiteSuccess = (customerName: string) => {
    displayMessage.success(`Site "${customerName}" updated`);
    clearState();
    getBusinesses.query();
  }

  const disableSites = (business: BusinessDto) => {
    displayMessage.clear();
    setPopupState({ state: States.DisableSites, Business: business });
  }

  const enableSites = (business: BusinessDto) => {
    displayMessage.clear();
    setPopupState({ state: States.EnableSites, Business: business });
    useEnableSites.request();
  }

  const createBusiness = () => {
    displayMessage.clear();
    setPopupState({ state: States.CreateBusiness });
  }

  const addUserSuccess = (u: BusinessAddAdminUserDto, business: BusinessDto) => {
    displayMessage.success(`Added ${u.emailAddress} to business: ${business.name}`);
    clearState();
  }

  const importSuccess = (message: string) => {
    displayMessage.success(message);
    clearState();
  }

  const manageSitesSuccess = (message: string) => {
    displayMessage.success(message);
    clearState();
    getBusinesses.query();
  }

  const disableSitesSuccess = (message: string) => {
    displayMessage.success(message);
    clearState();
    getBusinesses.query();
  }

  const businessCreated = (message: string) => {
    displayMessage.success(message);
    clearState();
    getBusinesses.query();
  }

  const clearState = () => setPopupState({ state: States.None });

  return (
    <>
      <KaEmployeeTemplate>
        {popupState.state === States.CreateBusiness && <CreateBusiness
          kaUser={kahlerUser}
          onSuccess={businessCreated}
          onHide={clearState} />}
        {popupState.state === States.AddUser && <BusinessAddUser
          kaUser={kahlerUser}
          business={popupState.Business}
          onSuccess={addUserSuccess}
          onHide={clearState} />}
        {popupState.state === States.ImportProducts && <ImportBusinessEntity
          business={popupState.Business}
          onSuccess={importSuccess}
          onHide={clearState}
          entityType="products"
          entityDisplayName="Products" />}
        {popupState.state === States.ImportDrivers && <ImportBusinessEntity
          business={popupState.Business}
          onSuccess={importSuccess}
          onHide={clearState}
          entityType="drivers"
          entityDisplayName="Drivers" />}
        {popupState.state === States.ImportCustomers && <ImportBusinessEntity
          business={popupState.Business}
          onSuccess={importSuccess}
          onHide={clearState}
          entityType="customers"
          entityDisplayName="Customers" />}
        {popupState.state === States.ImportCarriers && <ImportBusinessEntity
          business={popupState.Business}
          onSuccess={importSuccess}
          onHide={clearState}
          entityType="carriers"
          entityDisplayName="Carriers" />}
        {popupState.state === States.ManageSites && <ManageSites
          kaUser={kahlerUser}
          business={popupState.Business}
          onSuccess={manageSitesSuccess}
          onHide={clearState} />}
        {popupState.state === States.CreateSite && <CreateEditSite
          business={popupState.Business}
          onSuccess={created => createSiteSuccess(created.customerName)}
          onHide={clearState} />}
        {popupState.state === States.EditSite && <CreateEditSite
          site={popupState.Site}
          onSuccess={updated => editSiteSuccess(updated.customerName)}
          onHide={clearState} />}
        {popupState.state === States.DisableSites && <DisableSites
          business={popupState.Business}
          onSuccess={disableSitesSuccess}
          onHide={clearState} />}
        <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
        <QueryView
          query={getBusinesses}
          renderError={() => <h3>Something went wrong loading the list of businesses</h3>}
          renderData={businesses => <BusinessList
            user={kahlerUser}
            businesses={businesses}
            expanded={expanded}
            addUserClicked={addUserToBusiness}
            importProducts={importProducts}
            importDrivers={importDrivers}
            importCustomers={importCustomers}
            importCarriers={importCarriers}
            manageSites={manageSites}
            createSite={createSite}
            editSite={editSite}
            disableSites={disableSites}
            enableSites={enableSites}
            createBusiness={createBusiness}
          />}
        />
      </KaEmployeeTemplate>
    </>
  );
}

export { BusinessPage };
